import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { AppRoutes } from "@/routes";
import { Navbar } from "@components/layout/Navbar";
import { useTenant } from "@providers/TenantProvider";
import { useAppStore } from "@stores/appStore";
import {
  apiClient,
  AUTH_COOKIES,
  storeAccessToken,
  isAuthenticated,
} from "@lib/api";
import { ENV } from "@lib/env";

/**
 * Main App component
 */
function App() {
  const location = useLocation();
  const { config } = useTenant();
  const setUserEmail = useAppStore((state) => state.setUserEmail);

  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const isSignedOutPage = location.pathname === "/signed-out";

  // Handle OAuth login flow
  useEffect(() => {
    if (isSignedOutPage) {
      setIsLoading(false);
      setIsInitialized(true);
      return;
    }

    async function initAuth() {
      const debugLog: string[] = JSON.parse(localStorage.getItem('__auth_debug') || '[]');
      const log = (msg: string) => { debugLog.push(`${new Date().toISOString()} ${msg}`); localStorage.setItem('__auth_debug', JSON.stringify(debugLog)); console.log("[DEBUG AUTH]", msg); };

      // Check if already authenticated
      const authenticated = isAuthenticated();
      log(`isAuthenticated: ${authenticated}, cookies: ${document.cookie.split(';').map(c => c.trim().split('=')[0]).filter(Boolean).join(',')}`);
      if (authenticated) {
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }

      // Check for sessionId from OAuth callback
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("sessionId");
      log(`sessionId from URL: ${sessionId}, fullURL: ${window.location.href}`);

      if (!sessionId) {
        log("No sessionId, redirecting to login");
        initiateLogin();
        return;
      }

      try {
        // Fetch tokens from OAuth server
        const url = `${ENV.VITE_OAUTH_SERVER_URL}${ENV.VITE_OAUTH_CLIENT_REDIRECT_PATH}/sessions/${sessionId}`;
        log(`Fetching tokens from: ${url}`);

        const response = await fetch(url);
        log(`Token response status: ${response.status}`);
        const data = await response.json();
        log(`Token response keys: ${Object.keys(data).join(',')}, hasAccessToken: ${!!data.accessToken}`);

        if (data.accessToken) {
          storeAccessToken(data.accessToken, data.refreshToken, "Bearer");
          log(`Tokens stored, cookies now: ${document.cookie.split(';').map(c => c.trim().split('=')[0]).filter(Boolean).join(',')}`);

          // Remove sessionId from URL
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete("sessionId");
          window.history.replaceState({}, "", newUrl.toString());

          setIsInitialized(true);
        } else {
          log(`No accessToken in response, data: ${JSON.stringify(data)}`);
          initiateLogin();
        }
      } catch (error) {
        log(`Auth initialization failed: ${error}`);
        initiateLogin();
      } finally {
        setIsLoading(false);
      }
    }

    initAuth();
  }, [isSignedOutPage]);

  // Fetch user preferences when authenticated
  useEffect(() => {
    if (!isInitialized || isSignedOutPage) return;

    async function fetchUserPreferences() {
      try {
        const response = await apiClient.get("/user-preference");
        setUserEmail(response.data.email);
      } catch (error) {
        console.error("Failed to fetch user preferences:", error);
      }
    }

    fetchUserPreferences();
  }, [isInitialized, isSignedOutPage, setUserEmail]);

  // Update document title based on tenant
  useEffect(() => {
    document.title = `Wint - ${config.branding.companyName} Dashboard`;
  }, [config.branding.companyName]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-sans">
      <Navbar isSignedOutPage={isSignedOutPage} />
      <main className="flex-1">
        <AppRoutes />
      </main>
    </div>
  );
}

/**
 * Initiate OAuth login flow
 */
function initiateLogin() {
  const csrf = crypto.randomUUID();
  Cookies.set(AUTH_COOKIES.LOGIN_CSRF, csrf, { sameSite: "Strict" });

  const params = new URLSearchParams({
    response_type: "code",
    client_id: ENV.VITE_OAUTH_CLIENT_ID,
    redirect_uri: `${ENV.VITE_OAUTH_SERVER_URL}${ENV.VITE_OAUTH_CLIENT_REDIRECT_PATH}?clientId=${ENV.VITE_OAUTH_CLIENT_ID}`,
    scope: ENV.VITE_OAUTH_CLIENT_SCOPE,
    state: csrf,
  });

  window.location.replace(`${ENV.VITE_OAUTH_LOGIN_URL}?${params.toString()}`);
}

export default App;
